import { useEffect, useRef, useState } from 'react'
import { Lock, RefreshCw, ShieldCheck, Zap } from 'lucide-react'
import type { AccountAccess, ModeSummary } from './access'
import './GoogleAds.css'

function timeAgo(iso: string): string {
  if (!iso) return ''
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  return `${hours} hour${hours === 1 ? '' : 's'} ago`
}

const CHIP: Record<string, { label: string; tip: string }> = {
  full: {
    label: 'Can make changes',
    tip: 'The agent has Standard or Admin access in Google Ads, so Live mode can apply changes here.',
  },
  read_only: {
    label: 'Report only',
    tip:
      'The advertiser gave the agent read-only access in Google Ads, so this account only gets recommendations. ' +
      'Ask them for Standard access to allow changes.',
  },
  no_access: {
    label: 'No access',
    tip: 'The agent can see this account in the list but cannot read its data. Check its access in Google Ads.',
  },
  unknown: {
    label: 'Access not checked',
    tip: 'Access could not be checked (for example, the account has no campaigns yet). It is checked again on every run.',
  },
}

/** Small chip next to an account name saying whether Live mode can change it. */
export function AccessChip({
  access,
  checking,
  onRecheck,
}: {
  access?: AccountAccess
  checking: boolean
  onRecheck: () => void
}) {
  if (!access) {
    return <span className="gads-chip checking">Checking access…</span>
  }
  const chip = CHIP[access.access] ?? CHIP.unknown
  const checked = access.checked_at ? `\n\nChecked ${timeAgo(access.checked_at)}.` : ''
  return (
    <span className={`gads-chip-wrap${checking ? ' rechecking' : ''}`} onClick={(e) => e.stopPropagation()}>
      <span className={`gads-chip ${access.access}`} title={chip.tip + checked}>
        {access.access === 'read_only' && <Lock size={11} aria-hidden />}
        {chip.label}
      </span>
      <button
        type="button"
        className="gads-recheck"
        onClick={onRecheck}
        disabled={checking}
        title={`Recheck access${access.checked_at ? ` (checked ${timeAgo(access.checked_at)})` : ''}`}
        aria-label="Recheck access"
      >
        <RefreshCw size={11} aria-hidden className={checking ? 'spinning' : undefined} />
      </button>
    </span>
  )
}

/** "How should the agent act?": Safe vs Live, with a plain-language summary and a confirm step for Live. */
export function ModeCard({
  safe,
  onChange,
  summary,
  budgetStepPct,
  portfolioCapPct,
}: {
  safe: boolean
  onChange: (safe: boolean) => void
  summary: ModeSummary
  budgetStepPct: number
  portfolioCapPct: number
}) {
  const [confirming, setConfirming] = useState(false)
  const liveDisabled = summary.allReportOnly

  // An account selection that leaves only report-only accounts makes Live meaningless.
  useEffect(() => {
    if (liveDisabled && !safe) onChange(true)
  }, [liveDisabled, safe, onChange])

  return (
    <div className="st-field gads-mode">
      <label className="gads-mode-title">How should the agent act?</label>
      <div className="gads-mode-options" role="radiogroup" aria-label="Agent mode">
        <button
          type="button"
          role="radio"
          aria-checked={safe}
          className={`gads-mode-option${safe ? ' selected' : ''}`}
          onClick={() => onChange(true)}
        >
          <span className="gads-mode-option-head">
            <ShieldCheck size={16} aria-hidden /> Safe mode
          </span>
          <span className="gads-mode-option-sub">Recommend only. Nothing in Google Ads changes.</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!safe}
          aria-disabled={liveDisabled}
          disabled={liveDisabled}
          className={`gads-mode-option live${!safe ? ' selected' : ''}`}
          onClick={() => !liveDisabled && safe && setConfirming(true)}
          title={liveDisabled ? 'All selected accounts gave report-only access.' : undefined}
        >
          <span className="gads-mode-option-head">
            <Zap size={16} aria-hidden /> Live mode
          </span>
          <span className="gads-mode-option-sub">Apply approved changes automatically.</span>
        </button>
      </div>
      <div className={`gads-mode-summary${!safe && summary.liveCount ? ' live' : ''}`} aria-live="polite">
        {summary.text}
        {liveDisabled && (
          <> Ask the advertisers for Standard access in Google Ads to enable Live mode.</>
        )}
      </div>

      {confirming && (
        <LiveConfirmDialog
          accounts={summary.liveCount}
          reportOnly={summary.reportOnlyCount}
          budgetStepPct={budgetStepPct}
          portfolioCapPct={portfolioCapPct}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false)
            onChange(false)
          }}
        />
      )}
    </div>
  )
}

function LiveConfirmDialog({
  accounts,
  reportOnly,
  budgetStepPct,
  portfolioCapPct,
  onCancel,
  onConfirm,
}: {
  accounts: number
  reportOnly: number
  budgetStepPct: number
  portfolioCapPct: number
  onCancel: () => void
  onConfirm: () => void
}) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    confirmRef.current?.focus()
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const where = accounts > 0 ? `${accounts} account${accounts === 1 ? '' : 's'}` : 'the selected accounts'
  return (
    <div className="gads-dialog-backdrop" onClick={onCancel}>
      <div
        className="gads-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gads-live-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="gads-live-title">Turn on Live mode?</h3>
        <p>
          On each run the agent may pause campaigns, change budgets (up to ±{budgetStepPct}% per campaign and
          +{portfolioCapPct}% account-wide) and pause keywords in {where}. Nothing is ever deleted.
        </p>
        <p>Google checks every change before it's applied, and every change is listed in the report.</p>
        {reportOnly > 0 && (
          <p className="gads-dialog-note">
            {reportOnly} report-only account{reportOnly === 1 ? '' : 's'} will keep getting recommendations only.
          </p>
        )}
        <div className="gads-dialog-actions">
          <button type="button" className="st-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="st-btn gads-btn-live" ref={confirmRef} onClick={onConfirm}>
            Turn on Live mode
          </button>
        </div>
      </div>
    </div>
  )
}

interface RunAccount {
  customer_id: string
  account_name: string
  mode: string
  error: string
}
interface RunDecision {
  customer_id: string
  status: string
}

const MODE_LABEL: Record<string, string> = { live: 'Live', safe: 'Safe', report_only: 'Report only' }

function outcome(account: RunAccount, decisions: RunDecision[]): { text: string; kind: 'ok' | 'error' | 'skipped' } {
  if (account.error) {
    if (account.error.includes('time limit')) return { text: 'Not reached this run (time limit)', kind: 'skipped' }
    return { text: account.error, kind: 'error' }
  }
  const mine = decisions.filter((d) => d.customer_id === account.customer_id)
  const count = (statuses: string[]) => mine.filter((d) => statuses.includes(d.status)).length
  const parts = [
    [count(['applied']), 'applied'],
    [count(['recommended', 'report_only']), 'recommended'],
    [count(['held', 'advisory']), 'held or for review'],
    [count(['validation_failed', 'failed']), 'rejected'],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, label]) => `${n} ${label}`)
  return { text: parts.length ? parts.join(' · ') : 'No changes needed', kind: 'ok' }
}

/** Per-account result of a Run now, shown above the full report text. */
export function RunOutcomeTable({ accounts, decisions }: { accounts: RunAccount[]; decisions: RunDecision[] }) {
  if (!accounts.length) return null
  return (
    <div className="gads-outcome-wrap">
      <table className="gads-outcome">
        <thead>
          <tr>
            <th>Account</th>
            <th>Mode</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => {
            const o = outcome(a, decisions)
            return (
              <tr key={a.customer_id}>
                <td>
                  {a.account_name}
                  {a.account_name !== a.customer_id && <span className="gads-muted"> {a.customer_id}</span>}
                </td>
                <td>{MODE_LABEL[a.mode] ?? '—'}</td>
                <td className={`gads-outcome-${o.kind}`}>
                  {o.kind === 'ok' ? '✓ ' : o.kind === 'error' ? '✗ ' : '⏱ '}
                  {o.text}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
